<template>
  <div class="vevo-wrapper">
    <!-- 上部ピンクバー: 戻る + 選択中の子の名前 -->
    <div class="vevo-topbar">
      <v-btn
        icon
        variant="text"
        size="small"
        class="vevo-back-btn"
        @click="goBack"
        :ripple="false"
      >
        <img :src="chevronLeftIcon" alt="back" class="vevo-back-icon" />
      </v-btn>
      <div class="vevo-topbar-title" @click="toggleChildSelector">
        <span>{{ selectedChildName || 'レポート' }}</span>
      </div>
      <div class="vevo-topbar-right" @click="toggleChildSelector">
        <v-icon v-if="children.length > 1" size="18" color="white">
          mdi-arrow-down-drop-circle-outline
        </v-icon>
        <v-spacer />
      </div>
    </div>

    <!-- 子セレクター (展開時のみ表示) -->
    <div v-if="showChildSelector && children.length > 1" class="vevo-child-selector">
      <div
        v-for="c in children"
        :key="c.childId"
        class="vevo-child-row"
        :class="{ active: c.childId === selectedChildId }"
        @click="selectChild(c.childId)"
      >
        <img
          class="vevo-child-avatar"
          :src="c.imageUrl || defaultChildIcon"
        />
        <span class="ml-3">{{ c.lastName }} {{ c.firstName }}</span>
      </div>
    </div>

    <!-- VEVOマスコット + 吹き出し -->
    <div class="vevo-intro">
      <img :src="mascotPeekImg" alt="VEVO" class="vevo-mascot" />
      <div 
        class="vevo-bubble"
        :style="{
          '--bubble-img': `url(${speechBubbleImg})`,
          '--bubble-img-full': `url(${speechBubbleFullImg})`
        }"
      >
        <div class="vevo-bubble-title">保護者のみなさま</div>
        <div class="vevo-bubble-body">
          こちらでは成長のようすとして<br class="vevo-br" />
          お子さまの日々の姿を<br class="vevo-br" />
          3ヶ月に一度お届けします！
        </div>
      </div>
    </div>

    <!-- レポート一覧 -->
    <div class="vevo-list-container">
      <div class="vevo-list-title">レポート一覧</div>

      <div v-if="isLoading" class="d-flex justify-center pa-6">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="reports.length === 0" class="vevo-empty">
        まだレポートが届いていません。<br />
        次のクォーターをお楽しみに！
      </div>

      <div
        v-for="r in reports"
        :key="`${r.childId}-${r.businessYear}-${r.quarter}`"
        class="vevo-card"
        @click="openDetail(r)"
      >
        <div class="vevo-card-icon-wrap">
          <img 
            :src="isUnread(r) ? envelopeIconUnread : envelopeIconRead" 
            alt="" 
            class="vevo-card-icon"
            :class="isUnread(r) ? 'vevo-icon-unread' : 'vevo-icon-read'"
          />
          <span v-if="isUnread(r)" class="vevo-new-badge">NEW</span>
        </div>
        <div class="vevo-card-text">
          <div class="vevo-card-date">{{ formatPublishedAt(r.publishedAt) }} 公開</div>
          <div class="vevo-card-title">{{ r.title }}</div>
        </div>
        <img :src="arrowPlayIcon" alt="" class="vevo-card-arrow" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import { familyModule } from "@/store/entityModules/family";
import { aiReportModule } from "@/store/entityModules/aiReport";
import type { AiReportListItem } from "@/api/ApiAiReport";

interface ChildLite {
  childId: number;
  firstName: string;
  lastName: string;
  imageUrl: string | null;
}

interface DataType {
  selectedChildId: number;
  children: ChildLite[];
  isLoading: boolean;
  showChildSelector: boolean;
  mascotPeekImg: string;
  envelopeIconRead: string;
  envelopeIconUnread: string;
  arrowPlayIcon: string;
  chevronLeftIcon: string;
  defaultChildIcon: string;
  speechBubbleImg: string;
  speechBubbleFullImg: string;
}

export default defineComponent({
  name: "Vevo",
  data: (): DataType => ({
    selectedChildId: NaN,
    children: [],
    isLoading: false,
    showChildSelector: false,
    mascotPeekImg: require("@/assets/vevo/mascot_peek.png"),
    envelopeIconRead: require("@/assets/vevo/envelope_icon_read.png"),
    envelopeIconUnread: require("@/assets/vevo/envelope_icon_unread.png"),
    arrowPlayIcon: require("@/assets/vevo/arrow_play.png"),
    chevronLeftIcon: require("@/assets/vevo/chevron_left.png"),
    defaultChildIcon: "https://via.placeholder.com/80x80?text=child",
    speechBubbleImg: require("@/assets/vevo/speech_bubble.png"),
    speechBubbleFullImg: require("@/assets/vevo/speech_bubble_full.png"),
  }),
  computed: {
    reports(): AiReportListItem[] {
      return aiReportModule.reports;
    },
    selectedChildName(): string {
      const c = this.children.find((c) => c.childId === this.selectedChildId);
      if (!c) return "";
      return `${c.lastName} ${c.firstName}`;
    },
  },
  watch: {
    selectedChildId(newId: number) {
      if (newId) this.loadReports();
    },
  },
  async mounted() {
    window.scrollTo(0, 0);
    await this.ensureFamily();
    this.children = this.deriveChildren();
    if (this.children.length > 0) {
      // 記録タブなど他画面から childId 付きで遷移してきたらそれを優先
      const queryChildId = parseInt(this.$route.query.childId as string, 10);
      const matched = this.children.find((c) => c.childId === queryChildId);
      this.selectedChildId = matched
        ? matched.childId
        : this.children[this.children.length - 1].childId;
    }
    if (this.selectedChildId) await this.loadReports();
  },
  methods: {
    async ensureFamily() {
      const me = (await import("@/store/entityModules/me")).meModule.me;
      if (!familyModule.family && me) {
        await familyModule.fetchFamilyById({
          familyId: me.families[0].familyId,
        });
      }
    },
    deriveChildren(): ChildLite[] {
      if (!familyModule.family) return [];
      // 卒園・退園のフィルタリングを削除し、すべての園児を表示
      return familyModule.family.children.map((c: any) => ({
        childId: c.childId,
        firstName: c.firstName,
        lastName: c.lastName,
        imageUrl: c.imageUrl,
      }));
      // 卒園・退園のフィルタリングを復活させる場合は以下のコードを使用
      // return familyModule.family.children
      //   .filter((c: any) => {
      //     let leaveDate: string | null = null;
      //     if (c.graduationDate && c.quitDate) {
      //       leaveDate = dayjs(c.graduationDate).isAfter(c.quitDate)
      //         ? c.quitDate
      //         : c.graduationDate;
      //     } else if (c.graduationDate) {
      //       leaveDate = c.graduationDate;
      //     } else if (c.quitDate) {
      //       leaveDate = c.quitDate;
      //     }
      //     return !leaveDate || dayjs(leaveDate).isAfter(dayjs());
      //   })
      //   .map((c: any) => ({
      //     childId: c.childId,
      //     firstName: c.firstName,
      //     lastName: c.lastName,
      //     imageUrl: c.imageUrl,
      //   }));
    },
    async loadReports() {
      // 子要素の切り替え時にスクロール位置を戻す
      window.scrollTo(0, 0);
      this.isLoading = true;
      try {
        await aiReportModule.fetchReports({ childId: this.selectedChildId });
      } finally {
        this.isLoading = false;
        // レポート一覧の描画完了後にもスクロール位置を戻す
        this.$nextTick(() => {
          window.scrollTo(0, 0);
        });
      }
    },
    selectChild(childId: number) {
      this.selectedChildId = childId;
      this.showChildSelector = false;
    },
    toggleChildSelector() {
      if (this.children.length > 1) {
        this.showChildSelector = !this.showChildSelector;
      }
    },
    openDetail(r: AiReportListItem) {
      this.$router.push({
        path: `/vevo/${r.childId}/${r.businessYear}/${r.quarter}`,
      });
    },
    isUnread(r: AiReportListItem): boolean {
      return aiReportModule.isUnread(r);
    },
    goBack() {
      // 記録タブからの遷移もあるため記録に戻す
      this.$router.replace("/record");
    },
    formatPublishedAt(value: string): string {
      return dayjs(value).format("YYYY年M月D日");
    },
  },
});
</script>

<style lang="scss" scoped>
$vevo-pink: #EE6090;
$vevo-pink-light: #FDE0E4;
$vevo-gray: #808080;

.vevo-wrapper {
  min-height: 100vh;
  padding-bottom: 80px;
  position: relative;
}

// 上部バー
.vevo-topbar {
  position: sticky;
  top: 0;
  z-index: 5;
  height: calc(52px + env(safe-area-inset-top));
  padding-top: env(safe-area-inset-top);
  background-color: $vevo-pink;
  display: flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;

  .vevo-back-btn {
    color: white !important;
    background: transparent !important;
    box-shadow: none !important;
    width: 40px !important;
    min-width: 40px !important;

    .vevo-back-icon {
      width: 24px;
      height: 24px;
      display: block;
    }
  }

  .vevo-topbar-title {
    flex: 1;
    text-align: center;
    line-height: 1.2;
    color: white;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    user-select: none;
  }

  .vevo-topbar-right {
    width: 40px;
    min-width: 40px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

// 子セレクター (ドロップダウン)
.vevo-child-selector {
  background-color: white;
  border: 1.5px solid $vevo-pink-light;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 6px 0;

  // 画面の上部バー直下に固定
  position: fixed;
  top: calc(52px + env(safe-area-inset-top) + 12px);
  left: 12px;
  right: 12px;
  z-index: 100;
  
  // 子要素が多い場合はセレクター内部でスクロール
  max-height: 60vh;
  overflow-y: auto;

  .vevo-child-row {
    display: flex;
    align-items: center;
    line-height: 1.2;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background-color: #fafafa;
    }

    &.active {
      color: $vevo-pink;
      font-weight: 600;
    }

    .vevo-child-avatar {
      width: 36px;
      height: 36px;
      min-width: 36px;
      min-height: 36px;
      border-radius: 50%;
      object-fit: cover;
    }
  }
}

// マスコット + 吹き出し
.vevo-intro {
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: 14px 12px 0 12px;
  min-height: 90px;
  justify-content: center;
  z-index: 2; // マスコットをレポート一覧より前面に表示
}

.vevo-mascot {
  margin-left: 10px;
  width: 96px;
  height: auto;
  flex-shrink: 0;
  align-self: flex-end;
  transform: translateY(18px); // Y軸(縦方向)にずらして一覧リストに重ねる
}

.vevo-bubble {
  margin: 0 5px;
  flex: 1;
  position: relative;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;

  // デフォルト（600px未満）の設定
  background-image: var(--bubble-img);
  aspect-ratio: 700 / 250;
  max-width: 300px;
  min-width: 200px;

  // 画像の中のテキスト調整
  padding: 14px 14px 18px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .vevo-bubble-title {
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #000;
    margin-bottom: 2px;
  }

  .vevo-bubble-body {
    text-align: center;
    font-size: 11px;
    font-weight: 400;
    color: #000;
    line-height: 1.5;
  }
}

// 画面サイズのレスポンシブ
@media (min-width: 600px) {
  .vevo-bubble {
    background-image: var(--bubble-img-full);
    aspect-ratio: 700 / 120;
    max-width: 550px;

    // 画像の中のテキスト調整
    padding: 10px 14px 18px 14px;

    .vevo-bubble-body {
      .vevo-br {
        display: none; // 改行タグの削除
      }
    }
  }
}

// リストコンテナ
.vevo-list-container {
  background-color: $vevo-pink-light;
  border-radius: 18px;
  margin: 12px 12px 0 12px;
  padding: 14px 14px 18px 14px;
  position: relative;
  z-index: 1; // マスコットより背面に表示
}

.vevo-list-title {
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 12px;
}

.vevo-empty {
  text-align: center;
  color: $vevo-gray;
  font-size: 13px;
  padding: 24px 0;
}

// レポートカード
.vevo-card {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 14px;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:last-child {
    margin-bottom: 0;
  }
}

.vevo-card-icon-wrap {
  position: relative;
  width: 70px;
  height: 70px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vevo-card-icon {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

// 既読アイコン
.vevo-icon-read {
  width: 70px;
  aspect-ratio: 1 / 1;
}

// 未読アイコン
.vevo-icon-unread {
  width: 65px;
  aspect-ratio: 1.37 / 1;
}

.vevo-new-badge {
  position: absolute;
  left: 12.5px;
  bottom: 20px;
  background-color: $vevo-pink;
  color: white;
  font-size: 13px;
  font-weight: 700;
  padding: 3px 8px 3px 9px;
  border-radius: 10px;
  line-height: 1.2;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.vevo-card-text {
  flex: 1;
  margin-left: 10px;
  min-width: 0;
}

.vevo-card-date {
  font-size: 13px;
  color: $vevo-gray;
  font-weight: 400;
  line-height: 1.4;
}

.vevo-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  line-height: 1.3;
  margin-top: 2px;
}

.vevo-card-arrow {
  width: 25px;
  height: 25px;
  flex-shrink: 0;
  margin-left: 6px;
}
</style>
